import { useEffect, useState, useRef } from "react";
import HomePage from "./HomePage";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { scanQuotaService } from "@/services/scanQuotaService";
import { toast } from "@/components/ui/use-toast";

const Index = () => {
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const initAttemptedRef = useRef(false);

  useEffect(() => {
    // Check if terms have been accepted
    const acceptedTerms = localStorage.getItem('termsAccepted') === 'true';
    setTermsAccepted(acceptedTerms);
    
    if (!acceptedTerms) {
      setShowTermsDialog(true);
    }
    
    // Careful initialization of scan quota service
    const initQuotaService = async () => {
      if (initAttemptedRef.current) return;
      initAttemptedRef.current = true;
      
      try {
        console.log("Initializing scan quota service on Index page");
        // Add a small delay to ensure Firebase auth is ready before fetching
        setTimeout(async () => {
          await scanQuotaService.getQuota(true);
          console.log("Successfully initialized quota service");
        }, 1500);
      } catch (error) {
        console.error("Error initializing scan quota service:", error);
      }
    };
    
    // Only initialize after terms are accepted and user has interacted with the page
    if (acceptedTerms) {
      initQuotaService();
    }
    
    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("Auth state changed in Index:", user ? "logged in" : "logged out");
      
      // Update document title
      document.title = "PokeScan - Pokémon Scanner";
      
      // On auth change, invalidate the cache
      scanQuotaService.invalidateCache();
    });
    
    return () => {
      unsubscribe();
    };
  }, [termsAccepted]); // Added termsAccepted as dependency

  const handleAcceptTerms = () => {
    localStorage.setItem('termsAccepted', 'true');
    setTermsAccepted(true);
    setShowTermsDialog(false);
    
    // Try to initialize quota after terms acceptance
    console.log("Initializing quota after terms acceptance");
    // Add a small delay to ensure everything is ready
    setTimeout(() => {
      scanQuotaService.getQuota(true).catch(error => {
        console.error("Error getting quota after terms acceptance:", error);
      });
    }, 1500);
  };
  
  if (!termsAccepted && showTermsDialog) {
    return (
      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center space-y-4 p-4">
            <h2 className="text-xl font-bold">Welcome to PokeScan!</h2>
            <p>Before you begin, please review and accept our Terms of Service and Privacy Policy.</p>
            
            <div className="max-h-60 overflow-y-auto text-left text-sm p-3 border border-gray-200 rounded bg-gray-50">
              <p className="mb-3"><strong>Important Notice:</strong> PokeScan provides card valuations that are estimates only and do not constitute professional appraisals. Values may vary from actual market prices.</p>
              <p className="mb-3">By using PokeScan, you agree that:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Card valuations are provided "as is" without warranty of any kind</li>
                <li>PokeScan is not liable for any damages resulting from reliance on our valuations</li>
                <li>You have read and agree to our full <a href="/terms" className="text-pokemon-blue underline" target="_blank">Terms of Service</a> and <a href="/privacy" className="text-pokemon-blue underline" target="_blank">Privacy Policy</a></li>
              </ul>
            </div>
            
            <Button className="w-full" onClick={handleAcceptTerms}>
              <Check className="mr-2 h-4 w-4" /> I Accept
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  return <HomePage />;
};

export default Index;
